import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineLockClosed, HiOutlineChartBar, HiOutlineArrowRight, HiOutlineCheckCircle } from 'react-icons/hi2';
import { HiOutlineRocketLaunch } from 'react-icons/hi2';
import { FaHandshake, FaUserCircle } from 'react-icons/fa';

// Animated Counter Component
const AnimatedCounter = ({ target, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      // smooth ease-out calculation
      const easeProgress = 1 - Math.pow(1 - progress, 5);
      
      setCount(Math.floor(easeProgress * target));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [target, duration]);

  return <>{count}{suffix}</>;
};

const AboutPage = () => {
  const stats = [
    { target: 250, suffix: '+', label: 'Projects Delivered' },
    { target: 50, suffix: '+', label: 'Enterprise Clients' },
    { target: 99, suffix: '%', label: 'Client Satisfaction' },
    { target: 10, suffix: '+', label: 'Years Experience' },
  ];

  const values = [
    { icon: <HiOutlineLockClosed />, title: 'Security-First', desc: 'Every solution is built with enterprise-grade security and compliance from day one.' },
    { icon: <HiOutlineChartBar />, title: 'Measurable ROI', desc: 'We focus on delivering quantifiable business outcomes, not just technology.' },
    { icon: <FaHandshake />, title: 'Partnership', desc: 'We work as an extension of your team, ensuring knowledge transfer and long-term success.' },
    { icon: <HiOutlineRocketLaunch />, title: 'Production Grade', desc: 'No prototypes or demos—we build AI systems designed for enterprise scale and reliability.' },
  ];

  const team = [
    { name: 'Rajesh Kumar', role: 'CEO & Founder', bio: '15+ years in AI leadership and enterprise digital transformation' },
    { name: 'Priya Sharma', role: 'Chief Technology Officer', bio: 'Expert in cloud architecture, MLOps, and scalable AI systems' },
    { name: 'Amit Patel', role: 'Head of AI Engineering', bio: 'Full-stack AI/ML with a focus on NLP and GenAI solutions' },
    { name: 'Neha Gupta', role: 'VP of Client Success', bio: 'Driving enterprise adoption and measurable business outcomes' },
  ];

  return (
    <>
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            <div className="hero-text">
              <div className="section-label">Our Story</div>
              <h1>About Disen Systems</h1>
              <p>We're on a mission to make enterprise AI practical, secure, and measurable.</p>
            </div>
          </div>
        </div>
        <div className="hero-image-wrapper">
          <img
            src="/images/about-hero.png"
            alt="Disen Systems Team Collaboration & Vision"
          />
        </div>
      </section>

      {/* Stats */}
      <section className="section section-darker">
        <div className="container">
          <div className="stats-grid" style={{ gap: 28, textAlign: 'center' }}>
            {stats.map((s, i) => (
              <div key={i} className={`fade-in-up-delay${i + 1}`} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
                borderRadius: 16, padding: '40px 20px',
              }}>
                <div style={{ fontSize: 48, fontWeight: 800, color: 'var(--cyan)', marginBottom: 8, fontFamily: "'Space Grotesk', sans-serif" }}>
                  <AnimatedCounter target={s.target} suffix={s.suffix} />
                </div>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)', fontWeight: 500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section section-dark">
        <div className="container">
          <div className="architecture-section">
            <div>
              <div className="section-label">Our Mission</div>
              <h2 style={{ fontSize: 36, fontWeight: 700, marginBottom: 20 }}>
                Bridging the gap between AI ambition and production reality
              </h2>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: 20 }}>
                Disen Systems was founded with a singular focus: helping enterprises move AI from experimentation to production. We saw too many organizations stuck in pilot purgatory, unable to scale their AI investments.
              </p>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                Our team combines deep technical expertise with strategic business acumen, ensuring every AI solution we deliver is secure, scalable, and aligned with measurable business outcomes.
              </p>
            </div>
            <div className="arch-image">
              <img src="/images/ai-solutions-hero.png" alt="Our Mission" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section section-darker">
        <div className="container">
          <div className="section-header">
            <div className="section-label">Core Principles</div>
            <h2>Our Values</h2>
          </div>
          <div className="services-grid">
            {values.map((v, i) => (
              <div className="service-card" key={i}>
                <div className="service-icon">{v.icon}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      {/* <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-label">Leadership</div>
            <h2>Our Team</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 28 }}>
            {team.map((m, i) => (
              <div key={i} className={`fade-in-up-delay${i + 1}`} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
                borderRadius: 16, padding: 32, textAlign: 'center', transition: 'all 0.4s',
              }}>
                <div style={{
                  width: 100, height: 100, borderRadius: '50%', background: 'var(--gradient-violet)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px', boxShadow: '0 8px 25px var(--violet-glow)',
                }}>
                  <FaUserCircle size={54} color="rgba(255,255,255,0.85)" />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>{m.name}</h3>
                <div style={{ color: 'var(--cyan)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                  {m.role}
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{m.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Join our growing team</h2>
            <p>We're always looking for talented engineers, data scientists, and AI strategists.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary">Get In Touch <HiOutlineArrowRight /></Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
